import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';

import Footer from '../../components/common/Footer';
import AppSnackbar from '../../components/common/popups/AppSnackbar';
import Navbar from '../../components/common/Navbar';
import ParentStudentRegistrationModal from '../../components/parent/ParentStudentRegistrationModal';
import useParentStudents from '../../hooks/useParentStudents';

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'S';

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

function StudentCard({ student, index, onClick }) {
  const isEven = index % 2 === 0;
  const accent = isEven ? '#f6911d' : '#a46cff';

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative overflow-hidden rounded-[14px] border border-white/[0.08] bg-black px-5 py-5 text-left transition-colors hover:border-[#f6911d]/40"
    >
      <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:gap-6">
        <div
          className={`relative flex h-[74px] w-[74px] items-center justify-center rounded-full border text-[24px] font-semibold ${
            isEven
              ? 'bg-[#f28c18] border-[#f28c18] text-white'
              : 'bg-[#6f38c8] border-[#6f38c8] text-white'
          }`}
        >
          {getInitials(student.displayName)}
          <span className="absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-[#171717] bg-[#38d15a]" />
        </div>

        <div className="min-w-0 lg:flex-[0.75]">
          <div className="flex items-center gap-4">
            <div className="min-w-0">
              <h3 className="truncate text-[18px] font-semibold text-white">
                {student.displayName}
              </h3>
            </div>
          </div>
        </div>

        <div className="hidden h-20 w-px bg-white/10 lg:block" />

        <div className="grid flex-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:items-center">
          <div className="flex items-center gap-3 sm:justify-self-start">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-xl border"
              style={{ borderColor: accent, color: accent }}
            >
              <AssignmentOutlinedIcon sx={{ fontSize: 22, color: 'currentColor' }} />
            </span>
            <div>
              <p className="text-sm text-white/55">Active classes</p>
              <p
                className="mt-1 text-[15px] font-semibold"
                style={{ color: isEven ? '#f6911d' : '#46d764' }}
              >
                {student.activeCount}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:justify-self-start">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-xl border"
              style={{ borderColor: accent, color: accent }}
            >
              <AssignmentOutlinedIcon sx={{ fontSize: 22, color: 'currentColor' }} />
            </span>
            <div>
              <p className="text-sm text-white/55">Pending classes</p>
              <p
                className="mt-1 text-[15px] font-semibold"
                style={{ color: isEven ? '#f6911d' : '#46d764' }}
              >
                {student.pendingCount}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span
              className="flex h-10 w-10 items-center justify-center rounded-xl border"
              style={{ borderColor: accent, color: accent }}
            >
              <CalendarMonthOutlinedIcon sx={{ fontSize: 22, color: 'currentColor' }} />
            </span>
            <div>
              <p className="text-sm text-white/55">Last Login</p>
              <p className="mt-1 text-[15px] font-semibold text-white">
                {isEven ? 'Today, 9:15 AM' : 'Yesterday, 7:45 PM'}
              </p>
            </div>
          </div>
          <div className="sm:col-span-2 lg:col-span-1 lg:justify-self-end">
            <div className="inline-flex h-[48px] w-full items-center justify-center gap-3 rounded-xl border border-[#F6911D] px-5 text-base font-semibold text-[#F6911D] transition-colors hover:bg-[#F6911D]/8 sm:w-auto sm:min-w-[120px]">
              <span>Classes</span>
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

const ParentStudents = () => {
  const navigate = useNavigate();
  const { students, loading, error, refresh } = useParentStudents();
  const [modalOpen, setModalOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const openStudent = (student) => {
    navigate(`/parent/students/${student.routeId}`, { state: { student } });
  };

  const handleCreated = async () => {
    await refresh();
    setSnackbar({ open: true, message: 'Student registered successfully.', severity: 'success' });
  };

  const templateStudents = students.map((student) => ({
    ...student,
    activeCount: toNumber(student?.classes?.activeCount ?? student?.classes?.active_count),
    pendingCount: toNumber(student?.classes?.pendingCount ?? student?.classes?.not_active_count),
  }));

  return (
    <div className="min-h-screen bg-[#0c0c0c] text-white">
      <Navbar />

      <main className="px-4 py-8 sm:px-6 lg:px-10 lg:py-10">
        <section className="mx-auto max-w-[1214px] rounded-[18px] border border-white/40 bg-[#151515] px-5 py-6 sm:px-8 sm:py-8 lg:px-10 lg:py-10">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <button
                type="button"
                onClick={() => navigate('/parent')}
                className="inline-flex items-center gap-2 text-base font-medium text-[#f6911d] hover:text-[#ffb24f]"
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 16 }} />
                <span>Back</span>
              </button>

              <h1 className="mt-6 text-[34px] font-semibold leading-tight text-white sm:text-[36px]">
                My Children
              </h1>
              <p className="mt-4 max-w-[420px] text-base leading-7 text-white/65 sm:text-[18px]">
                Manage and access all the student accounts linked to your profile.
              </p>
            </div>
          </div>

          {error ? (
            <div className="mt-6 rounded-[14px] border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {error}
            </div>
          ) : null}

          <div className="mt-8 space-y-4">
            {loading ? (
              <div className="rounded-[14px] border border-white/10 bg-black px-5 py-10 text-center text-white/60">
                Loading students...
              </div>
            ) : templateStudents.length > 0 ? (
              <div className="grid gap-4">
                {templateStudents.map((student, index) => (
                  <StudentCard
                    key={student.id ?? `${student.displayName}-${index}`}
                    student={student}
                    index={index}
                    onClick={() => openStudent(student)}
                  />
                ))}
              </div>
            ) : (
              <article className="rounded-[14px] border border-dashed border-white/20 bg-black px-5 py-8 text-center">
                <p className="text-base font-semibold text-white">No students have been added yet.</p>
                <p className="mt-2 text-sm text-white/60">Click &quot;Link New Student&quot; to register your first student.</p>
                <button
                  type="button"
                  onClick={openModal}
                  className="mt-5 inline-flex items-center gap-2 rounded-md border border-[#f6911d] px-4 py-2 text-sm font-medium text-[#f6911d] hover:bg-[#f6911d]/8"
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                  <span>Link New Student</span>
                </button>
              </article>
            )}

            <article className="rounded-[16px] border border-dashed border-white/25 bg-[#141414] px-5 py-5 sm:px-6 sm:py-6">
              <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-full bg-white/[0.08] text-[#F6911D]">
                    <AddIcon sx={{ fontSize: 34 }} />
                  </div>

                  <div>
                    <h3 className="text-[18px] font-semibold text-white">Link another student</h3>
                    <p className="mt-2 max-w-[420px] text-[15px] leading-7 text-white/60">
                      Add a new student to access their learning dashboard.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={openModal}
                  className="inline-flex h-[46px] items-center justify-center gap-2 rounded-xl border border-[#F6911D] px-5 text-base font-semibold text-[#F6911D] transition-colors hover:bg-[#F6911D]/8 sm:min-w-[190px]"
                >
                  <AddIcon sx={{ fontSize: 22 }} />
                  <span>Link New Student</span>
                </button>
              </div>
            </article>

            <article className="rounded-[16px] border border-white/[0.08] bg-[#171717] px-5 py-5 sm:px-6 sm:py-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#F6911D] text-[#F6911D]">
                    <span className="text-lg">✓</span>
                  </div>

                  <div>
                    <h3 className="text-[18px] font-semibold text-white">Your data is safe with us</h3>
                    <p className="mt-1 max-w-[620px] text-[15px] leading-7 text-white/60">
                      We use industry-standard security to protect your children&apos;s information.
                    </p>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>

      <Footer />

      <ParentStudentRegistrationModal open={modalOpen} onClose={closeModal} onSuccess={handleCreated} />
      <AppSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </div>
  );
};

export default ParentStudents;
